@import '../../../../navbars/type-1/index';

$component-class: 'sn-navbar-type-1';

// =============================================================================
// SSR Transition Strategy: Instant Switch
// =============================================================================
// 1. Placeholder renders during SSR, occupies layout space
// 2. Full navbar renders hidden on client
// 3. Once navbar is hydrated, instantly show navbar and hide placeholder
// No animations - instant switch to prevent any visual artifacts.
// NOTE: All placeholder styling (base display, sticky/relative, z-index, and the
// [hidden] swap rule) lives in the single critical shell sheet
// source/components/product/signnow/navbar/type-1-placeholder/_shell.scss
// (imported by source/global.scss) — not here — to avoid cross-component
// duplication of placeholder rules in the real navbar's stylesheet.
// =============================================================================

// =============================================================================
// Host state rules — MUST stay at top level (not nested under .#{$component-class}).
// The host element only carries the bare `sn-navbar-type-1` class in the loading/
// ready states, and never has a `.sn-navbar-type-1` ANCESTOR. Nesting these under
// `.#{$component-class}` produced a descendant selector
// (`.sn-navbar-type-1 sn-navbar-type-1.--ssr-deferred`) that never matched, so the
// deferred/loading navbar stayed `display:inline` and rendered on top of the
// placeholder — two navbars at once. Targeting the element directly fixes it.
// =============================================================================

// SSR deferred state - completely hidden, no space
sn-navbar-type-1.sn-navbar-type-1--ssr-deferred {
  display: none !important;
}

// Loading state - navbar hidden and not in document flow
// CRITICAL: Hide all dropdown menus to prevent FOUC before styles are applied
sn-navbar-type-1.sn-navbar-type-1--loading {
  display: none !important;
}

// Ready state - navbar visible and sticky.
// Sticky positioning is on the OUTER wrapper (sn-navbar-type-1), NOT on the inner navbar-type-1.
// The inner navbar-type-1 has sticky="always" for scroll/box-shadow logic,
// but its .is-sticky-host class is overridden here to prevent double sticky.
sn-navbar-type-1.sn-navbar-type-1--ready {
  display: block;
  position: sticky;
  top: 0;
  visibility: visible;
  width: auto;
  height: auto;
  overflow: visible;
  clip: auto;
  pointer-events: auto;
  z-index: 105;

  // Override inner navbar sticky: the outer wrapper handles sticky positioning.
  // Without this, both elements would be position: sticky (double sticky).
  navbar-type-1.is-sticky-host {
    position: static;
    z-index: auto;
  }

  // When search is opened, increase z-index to appear above google-button (z-index: 104)
  &.sn-navbar-type-1--search-opened {
    z-index: 106;
  }
}

// Keep navbar above social auth button when any dropdown is opened.
sn-navbar-type-1.sn-navbar-type-1--ready:has(.me-dropdown__menu--open) {
  z-index: 106;
}

// Opt the navbar host out of the below-fold content-visibility optimization.
// The navbar is functionally an above-the-fold sticky element, but the SSR swap keeps the
// real navbar `display: none` at first paint (the placeholder occupies the viewport), so
// server-side viewport analysis misclassifies the real navbar as below-fold and injects
// `data-below-fold`. On a block-level host that triggers `content-visibility: auto` (see
// `[data-below-fold]` in source/global.scss), whose implied `contain: paint` clips anything
// overflowing the bar's box — trapping the dropdown mega-menus and the mobile menu inside
// the bar so they never paint. Opting out restores normal painting of these overlays.
sn-navbar-type-1[data-below-fold] {
  content-visibility: visible;
}

.#{$component-class} {
  &--nav {
    margin: 0 auto;
  }

  .navbar-type-1__container--mobile .navbar-type-1__mobile-content {
    padding-top: 40px;
  }

  .sn-navbar-search {
    &__field {
      max-width: $navbar-max-width;

      // Keep the mobile field padding (0 15px) across the whole burger-menu range;
      // only center it once the navbar is truly desktop (> 960px).
      @include desktop-lg {
        margin: 0 auto;
      }
    }

    &__clean {
      display: none;

      // The clear (×) control is a desktop-search affordance — show it only above
      // the navbar's collapse breakpoint, not in the burger-menu search (≤ 960px).
      @include desktop-lg {
        display: block;
      }
    }
  }

  .navbar-type-1__container {
    padding-right: var(--spacing-md);
    padding-left: var(--spacing-md);
    max-width: $navbar-max-width;

    &--mobile {
      border-top: none;

      [slot="area-navbar-mobile"] {
        align-items: flex-start;

        .me-button {
          justify-content: space-between;
          padding-top: 0;
          padding-bottom: 0;
          font-size: 20px;
          font-weight: 600;
        }
      }

      // Indent expanded submenu items slightly so they read as nested under the
      // section trigger (matches the mobile layout across the whole burger range).
      .me-dropdown__menu {
        padding-left: var(--spacing-md);
      }
    }
  }

  // NOTE: placeholder base styles and the pre-hydration 960px collapse rule used
  // to live here, but nested under `.#{$component-class}` they compiled to
  // never-matching descendant selectors (`.sn-navbar-type-1 sn-navbar-placeholder-type-1`
  // and `.sn-navbar-type-1 sn-navbar-type-1 …` — the exact footgun documented
  // just below). They were dead: the placeholder is a SIBLING of this host, and
  // the real navbar stays display:none until hydration adds `is-mobile-view`
  // (handled by the base sheet), so it never needs a CSS-only 960px rule. The
  // placeholder's live 960px rule lives in the critical base mirror
  // (navbars/type-1/index.scss). Removed to avoid dead-code drift.

  // Content styling for everything rendered inside the navbar host.
  // MUST use `&` (not a nested `sn-navbar-type-1` element selector). The host
  // element carries the `.sn-navbar-type-1` class itself and contains an inner
  // `navbar-type-1` (NOT another `sn-navbar-type-1`). Nesting `sn-navbar-type-1`
  // here produced `.sn-navbar-type-1 sn-navbar-type-1 ...`, a descendant selector
  // that never matched, so every dropdown style below was dead (black CTA links,
  // unstyled titles, broken featured-card spacing). `&` keeps it as
  // `.sn-navbar-type-1 ...` so the rules apply to the host's descendants.
  & {
    .navbar-type-1 {
      border-bottom: none;
      z-index: 103;

      // Inline SVG logo styling (replaces me-logo component)
      a.me-logo {
        display: inline-flex;
        align-items: center;
        text-decoration: none;
        min-width: 92px;

        svg {
          display: block;
          height: auto;
          width: auto;
          max-height: $navbar-logo-height-mobile; // Mobile: match the placeholder's logo height.

          @include desktop {
            max-height: $navbar-logo-height-desktop;
          }
        }

        // Pin the height, don't just cap it. `me-logo` reserves a box for images that carry no
        // width/height attributes (`img:not([width])` — its CLS guard) with an explicit
        // `height: 36px/40px`, and an explicit height beats a `max-height` no matter the
        // specificity. That rendered the swapped-in navbar logo 40px tall against the SSR
        // placeholder's 42px, so the logo visibly shrank on hydration. Setting the same
        // navbar-metric height here keeps both bars identical and keeps the box reserved.
        img {
          width: auto;
          height: $navbar-logo-height-mobile; // Mobile: match the placeholder's logo height.
          max-height: $navbar-logo-height-mobile;

          @include desktop {
            height: $navbar-logo-height-desktop;
            max-height: $navbar-logo-height-desktop;
          }
        }
      }

      .me-logo {
        min-width: 92px;
      }

      // The <me-logo> custom-element host wraps an inline-flex <a>, whose line
      // box adds a ~4.5px baseline gap below the image. That inflates the host
      // to 46.5px and pushes the logo ~2.25px above the bar's vertical center.
      // Centering the host's content collapses the gap so the logo sits at the
      // exact center — matching the SSR placeholder (no vertical logo jump on swap).
      me-logo {
        display: flex;
        align-items: center;
      }

      &--logo-nav-first {
        .navbar-type-1__logo-column {
          margin-right: $spacing-xl;
        }

        .navbar-type-1__nav-column {
          gap: $spacing-md;

          @include below($breakpoint-desktop-md + 58px) {
            gap: $spacing-2xs;
          }
        }
      }
    }

    .dropdown-menu-type-1 {
      gap: 0;
    }

    .dropdown-menu-type-2__navigation {
      flex: 0 0 72%;
      padding-top: map-get($map: $spacings, $key: xl);
      padding-bottom: map-get($map: $spacings, $key: xl);
      padding-left: map-get($map: $spacings, $key: xl);

      @include desktop {
        padding: map-get($map: $spacings, $key: 2xl) 0;
      }
    }

    .dropdown-menu-type-2__featured {
      flex: 0 0 28%;
      max-width: 28%;
      padding: map-get($map: $spacings, $key: 2xl) 0 map-get($map: $spacings, $key: 2xl) map-get($map: $spacings, $key: 2xl);

      .dropdown-menu-type-2__element-item .featured-card__title {
        font-size: map-get($font-sizes, md);
        font-weight: map-get($font-weights, bold);
        color: var(--color-neutral-black);
      }
    }

    .dropdown-menu-type-2__element-item:not(:last-child) {
      margin-bottom: map-get($map: $spacings, $key: lg);
    }

    .dropdown-menu-type-2__wrapper {
      border-bottom: none;
    }

    hero-header-type-1 {
      .picture {
        background-color: transparent;
      }
    }

    .dropdown-link-icon {
      path {
        fill: transparent;
      }
    }

    // Blue accent buttons (primary CTA links in dropdowns)
    .me-button.me-button--blue {
      color: $background-color-brand-primary;
      font-weight: map-get($font-weights, bold);

      .me-button__title {
        font-weight: map-get($font-weights, bold);
      }

      &::before {
        background-color: $background-color-brand-primary;
      }

      // Keep blue color on hover for both text and icon
      &:hover,
      &:focus {
        color: $background-color-brand-primary;

        .me-button__icon {
          color: $background-color-brand-primary;
        }
      }
    }

    // Bold font for specific buttons
    .me-button--bold .me-button__title {
      font-weight: map-get($font-weights, bold);
    }

    .dropdown-menu-type-2__featured-card {
      .featured-card__title {
        font-size: map-get($font-sizes, md);
        font-weight: map-get($font-weights, bold);
        color: var(--color-neutral-black);
      }

      .featured-card__description {
        font-size: map-get($font-sizes, sm);
        line-height: map-get($line-heights, lg);
        color: var(--color-neutral-black);
      }
    }

    .dropdown-menu-type-2__features {
      margin-bottom: 0;

      .featured-card__content {
        justify-content: space-between;
        margin-top: map-get($map: $spacings, $key: 2xl);

        .featured-card__text {
          flex: none;

          .me-button {
            padding: map-get($map: $spacings, $key: xs);
            border-color: $color-neutral-neutral;
            border-width: map-get($map: $border-widths, $key: sm);
            border-radius: 50%;
          }
        }
      }
    }

    .dropdown-menu-type-2__top-integrations {
      .featured-card__content {
        margin-top: map-get($map: $spacings, $key: lg);
      }

      .dropdown-menu-type-2__featured-list {
        display: flex;
        flex-direction: column;
        gap: map-get($map: $spacings, $key: lg);
      }
    }

    .dropdown-menu-type-2__documentation {
      margin-bottom: 0;

      .featured-card__content {
        justify-content: space-between;
        align-items: flex-end;
        margin-top: map-get($map: $spacings, $key: lg);

        .featured-card__picture svg {
          margin-left: -#{map-get($map: $spacings, $key: lg)};
          margin-bottom: -#{map-get($map: $spacings, $key: lg)};
        }

        .featured-card__text {
          flex: none;

          .me-button {
            padding: map-get($map: $spacings, $key: xs);
            border-color: $color-neutral-neutral;
            border-width: map-get($map: $border-widths, $key: sm);
            border-radius: 50%;
          }
        }
      }
    }

    // Search icon in navbar
    .search-icon {
      @include size(32px);
    }

    // Fix dropdown mega-menu positioning in editor context.
    // The editor may apply position:relative to me-dropdown elements for click
    // tracking / drag handles, which causes me-dropdown__menu (position:absolute)
    // to anchor to the trigger button instead of the full-width navbar-type-1
    // (position:relative). Force position:static so the dropdown always positions
    // relative to navbar-type-1 as intended.
    @include desktop {
      .navbar-type-1__nav-column me-dropdown {
        position: static !important;
      }
    }
  }
}

